import React from 'react';
import { Alert, Badge, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { auth } from '../../Api';
import { formatServerMessage } from '../../utils/serverErrors';
import { downloadPdf } from '../../utils/invoices';
import { shortDate } from '../../utils/billingOverview';
import { commissionStatus, markPaidProblem, todayIso } from '../../utils/commission';

// The super admin's Reseller commission section on the Billing page (laravel App\Commission::superAdminOverview):
// per reseller, commission owed (not invoiced yet), invoiced (awaiting payment) and paid, all time; the commission
// invoices resellers submitted, with the PDF, the reseller's "Pay with Paystack" page and Mark paid (the date and the
// Paystack reference). A commission invoice is also marked paid from FreeAgent once the payment is explained against
// its bill there, whichever comes first. onChanged: something was paid (the page loads again); onDialog(open).
class ResellerCommission extends React.Component {
	constructor(props) {
		super(props);
		this.state = { busy: null, error: '', notice: '', paying: null, paidOn: '', reference: '', problem: '' };
	}

	download(invoice) {
		this.setState({ busy: 'pdf' + invoice.id, error: '' });
		auth.getCommissionPdf({ id: invoice.id })
			.then((res) => {
				if (res.data.code == 200) {
					downloadPdf(res.data.file_name, res.data.pdf_base64);
					this.setState({ busy: null });
				} else {
					this.setState({ busy: null, error: formatServerMessage(res.data.message, 'The PDF could not be made.') });
				}
			})
			.catch(() => this.setState({ busy: null, error: 'The PDF could not be made. Please try again.' }));
	}

	open(invoice) {
		this.setState({ paying: invoice, paidOn: todayIso(), reference: '', problem: '', notice: '', error: '' });
		if (this.props.onDialog) {
			this.props.onDialog(true);
		}
	}

	close() {
		this.setState({ paying: null });
		if (this.props.onDialog) {
			this.props.onDialog(false);
		}
	}

	markPaid() {
		const { paying, paidOn, reference } = this.state;
		const problem = markPaidProblem(paidOn, reference, todayIso());
		if (problem) {
			this.setState({ problem });
			return;
		}
		this.setState({ busy: 'mark', problem: '' });
		auth.markCommissionPaid({ id: paying.id, paid_on: paidOn, reference: reference.trim() })
			.then((res) => {
				if (res.data.code == 200) {
					this.setState({ busy: null, notice: formatServerMessage(res.data.message, 'Marked paid.') });
					this.close();
					if (this.props.onChanged) {
						this.props.onChanged();
					}
				} else {
					this.setState({ busy: null, problem: formatServerMessage(res.data.message, 'It could not be marked paid.') });
				}
			})
			.catch(() => this.setState({ busy: null, problem: 'It could not be marked paid. Please try again.' }));
	}

	render() {
		const { commission } = this.props;
		const { busy, error, notice, paying, paidOn, reference, problem } = this.state;
		if (!commission || !commission.available) {
			return null;
		}
		return (
			<div className="billing-commission mt-4">
				<h5 className="mb-1">Reseller commission</h5>
				<p className="text-muted small mb-2">
					All time. Owed: earned on paid invoices, not invoiced by the reseller yet. Invoiced: on a commission invoice, not paid yet.
					Pay a commission invoice by card on the reseller's Paystack page, then explain the payment against its bill in FreeAgent
					(it is then marked paid here at the next FreeAgent sync) or press Mark paid.
				</p>
				{error && <Alert color="danger">{error}</Alert>}
				{notice && <Alert color="success" toggle={() => this.setState({ notice: '' })}>{notice}</Alert>}
				{commission.resellers.length === 0 ? <p className="text-muted small">No reseller has earned commission yet.</p> : (
					<Table size="sm" className="billing-companies billing-commission-table">
						<thead>
							<tr>
								<th>Reseller</th>
								<th className="text-right">Owed (USD)</th>
								<th className="text-right">Invoiced, unpaid</th>
								<th className="text-right">Paid</th>
								<th>Paystack link</th>
							</tr>
						</thead>
						<tbody>
							{commission.resellers.map((row) => (
								<tr key={row.id}>
									<td>{row.name}{row.deleted && <span className="text-muted"> (deleted)</span>}</td>
									<td className="text-right">{row.owed_usd}</td>
									<td className={'text-right' + (Number(row.invoiced_usd) > 0 ? ' text-warning' : '')}>{row.invoiced_usd}</td>
									<td className="text-right">{row.paid_usd}</td>
									<td>{row.has_paystack ? 'Saved' : <span className="text-muted">Not given yet</span>}</td>
								</tr>
							))}
						</tbody>
					</Table>
				)}
				<h6 className="mt-3 mb-1">Commission invoices</h6>
				{commission.invoices.length === 0 ? <p className="text-muted small">None submitted yet.</p> : (
					<Table size="sm" className="billing-companies billing-commission-table">
						<thead>
							<tr>
								<th>Invoice</th>
								<th>Reseller</th>
								<th>Date</th>
								<th>Due</th>
								<th className="text-right">Amount (USD)</th>
								<th>Status</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{commission.invoices.map((invoice) => {
								const status = commissionStatus(invoice, shortDate);
								return (
									<tr key={invoice.id}>
										<td>{invoice.number}</td>
										<td>{invoice.reseller}</td>
										<td>{shortDate(invoice.issue_date)}</td>
										<td>{shortDate(invoice.due_date)}</td>
										<td className="text-right">{invoice.total_usd}</td>
										<td>
											<Badge color={status.tone} title={invoice.payment_reference ? 'Reference: ' + invoice.payment_reference : ''}>{status.label}</Badge>
										</td>
										<td className="text-right text-nowrap">
											<Button size="sm" color="link" className="p-0 mr-2" disabled={busy === 'pdf' + invoice.id} onClick={() => this.download(invoice)}>PDF</Button>
											{invoice.status !== 'paid' && (
												<React.Fragment>
													<a className="btn btn-sm btn-primary mr-1" href={invoice.pay_url || invoice.paystack_url} target="_blank" rel="noopener noreferrer">Pay with Paystack</a>
													<Button size="sm" color="secondary" outline onClick={() => this.open(invoice)}>Mark paid</Button>
												</React.Fragment>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				)}
				{paying && (
					<Modal isOpen={true} toggle={() => this.close()}>
						<ModalHeader toggle={() => this.close()}>Mark {paying.number} paid</ModalHeader>
						<ModalBody>
							<p className="small">USD {paying.total_usd} to {paying.reseller}. Use this once you have paid it on the reseller's Paystack page.</p>
							<FormGroup>
								<Label for="commission-paid-on" className="small">Date it was paid</Label>
								<Input id="commission-paid-on" type="date" bsSize="sm" value={paidOn} max={todayIso()}
									onChange={(e) => this.setState({ paidOn: e.target.value, problem: '' })} />
							</FormGroup>
							<FormGroup>
								<Label for="commission-reference" className="small">Paystack reference</Label>
								<Input id="commission-reference" bsSize="sm" maxLength={255} value={reference} placeholder="As shown on the Paystack receipt"
									onChange={(e) => this.setState({ reference: e.target.value, problem: '' })} />
							</FormGroup>
							{problem && <div className="text-danger small">{problem}</div>}
						</ModalBody>
						<ModalFooter>
							<Button color="secondary" onClick={() => this.close()}>Cancel</Button>
							<Button color="primary" disabled={busy === 'mark'} onClick={() => this.markPaid()}>{busy === 'mark' ? 'Saving...' : 'Mark paid'}</Button>
						</ModalFooter>
					</Modal>
				)}
			</div>
		);
	}
}

export default ResellerCommission;
